import { Component, OnInit, Input, Output, ElementRef, ContentChild, ViewChild } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { CfCoreComponent } from '../core/core.component';
import { AvatarService } from '../../services/avatar-service/avatar.service';
import { AvatarModel } from '../../models/avatar/avatar.model';
import { AvatarStylingModel } from '../../models/avatar/avatar-styling.model';
import { AvatarTemplates } from '../../templates/avatar.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Avatar component based on cf-icon and cf-image componets</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-avatar [properties]="myAvatar" [styling]="myAvatarStyles"<code>></code><code>
 * <</code><code>/</code>cf-avatar<code>></code>
 * </pre>
 */
export class CfAvatarComponent extends CfCoreComponent implements OnInit {
/**
 * AvatarModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  type: 'image',
 *  url: 'this.myAvatarUrl',
 *  width: '70px',
 *  height: '70px',
 *  border: '5px solid green',
 *  borderRadius: '50%',
 *  shadow: '-3px -3px 3px 0px rgba(0, 150, 136, 0.3)'
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>type: string</h4>
 * Type of avatar. Can be: image, icon, text. Default: image.
 * <h4>name: string</h4>
 * The name of the person from the initials of which the avatar will be generated, based on <b>charsNumber</b>
 * <h4>url: string</h4>
 * Url for the image
 * <h4>text: string</h4>
 * Text to be used as avatar
 * <h4>iconName: string</h4>
 * Name of icon to be used for cf-icon
 * <h4>charsNumber: number</h4>
 * Number of initials simulators in the avatar. Default: 2.
 * <h4>color: string</h4>
 * The color that will be applied to text or icons
 * <h4>size: any</h4>
 * The size to be applied as the height and width of the avatar.
 * Required if you want use your avatar from other sites (Facebook, Google, etc). Default: 70px.
 * <h4>width: any</h4>
 * The width of the avatar. Has higher priority then <b>size</b>. Default: 70px.
 * <h4>height: any</h4>
 * The height of the avatar. Has higher priority then <b>size</b>. Default: 70px.
 * <h4>fontSize: string</h4>
 * Font size for the text (initials or custom text). Default: 30px.
 * <h4>border: string</h4>
 * Avatar border. Not specified by default.
 * <h4>borderRadius: string</h4>
 * Avatar border radius. Not specified by default.
 * <h4>background: string</h4>
 * Avatar background. Not specified by default.
 * <h4>backgroundImage: string</h4>
 * Avatar background-image. Not specified by default.
 * <h4>shadow: string</h4>
 * Avatar shadow. Not specified by default.
 * <h4>facebookId: string</h4>
 * Facebook ID
 * <h4>googleId: string</h4>
 * Google ID
 * <h4>twitterId: string</h4>
 * Twitter Handle
 * <h4>vkontakteId: string</h4>
 * VK ID
 * <h4>skypeId: string</h4>
 * Skype ID
 * <h4>gravatarId: string</h4>
 * email or md5 email related to gravatar
 * <h4>githubId: string</h4>
 * Github ID
 */
public properties: AvatarModel;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * Styling properties for main avatar container
 * <h4>image: ImageStylingModel</h4>
 * Styling properties for cf-image
 * <h4>icon: IconStylingModel</h4>
 * Styling properties for cf-icon
 * <h4>name: StylingModel</h4>
 * Styling properties for name
 * <h4>text: StylingModel</h4>
 * Styling properties for text
 */
public styling: AvatarStylingModel;
/**
 * Type of avatar. Can be: image, icon, text. Default: image.
 * @param {?} value
 * @return {?}
 */
set type(value: string) { this.properties.type = value; }
/**
 * The name of the person from the initials of which the avatar will be generated, based on <b>charsNumber</b>
 * @param {?} value
 * @return {?}
 */
set name(value: string) { this.properties.name = value; }
/**
 * Url for the image
 * @param {?} value
 * @return {?}
 */
set url(value: string) { this.properties.url = value; }
/**
 * Text to be used as avatar
 * @param {?} value
 * @return {?}
 */
set text(value: string) { this.properties.text = value; }
/**
 * Name of icon to be used for cf-icon
 * @param {?} value
 * @return {?}
 */
set iconName(value: string) { this.properties.iconName = value; }
/**
 * Number of initials simulators in the avatar. Default: 2.
 * @param {?} value
 * @return {?}
 */
set charsNumber(value: number) { this.properties.charsNumber = value; }
/**
 * The color that will be applied to text or icons
 * @param {?} value
 * @return {?}
 */
set color(value: string) { this.properties.color = value; }
/**
 * The size to be applied as the height and width of the avatar.
 * Required if you want use your avatar from other sites (Facebook, Google, etc). Default: 50px.
 * @param {?} value
 * @return {?}
 */
set size(value: any) { this.properties.size = value; }
/**
 * The width of the avatar. Has higher priority then <b>size</b>. Default: 50px.
 * @param {?} value
 * @return {?}
 */
set width(value: any) { this.properties.width = value; }
/**
 * The height of the avatar. Has higher priority then <b>size</b>. Default: 50px.
 * @param {?} value
 * @return {?}
 */
set height(value: any) { this.properties.height = value; }
/**
 * Font size for the text (initials or custom text). Default: 30px.
 * @param {?} value
 * @return {?}
 */
set fontSize(value: string) { this.properties.fontSize = value; }
/**
 * Avatar border. Not specified by default.
 * @param {?} value
 * @return {?}
 */
set border(value: string) { this.properties.border = value; }
/**
 * Avatar border radius. Not specified by default.
 * @param {?} value
 * @return {?}
 */
set borderRadius(value: string) { this.properties.borderRadius = value; }
/**
 * Avatar background. Not specified by default.
 * @param {?} value
 * @return {?}
 */
set background(value: string) { this.properties.background = value; }
/**
 * Avatar background-image. Not specified by default.
 * @param {?} value
 * @return {?}
 */
set backgroundImage(value: string) { this.properties.backgroundImage = value; }
/**
 * Avatar box-shadow. Not specified by default.
 * @param {?} value
 * @return {?}
 */
set shadow(value: string) { this.properties.shadow = value; }
/**
 * Facebook ID
 * @param {?} value
 * @return {?}
 */
set facebookId(value: string) {
    this.properties.facebookId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('facebook', this.properties.facebookId);
    }
  }
/**
 * Google ID
 * @param {?} value
 * @return {?}
 */
set googleId(value: string) {
    this.properties.googleId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('google', this.properties.googleId);
    }
  }
/**
 * Twitter Handle
 * @param {?} value
 * @return {?}
 */
set twitterId(value: string) {
    this.properties.twitterId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('twitter', this.properties.twitterId);
    }
  }
/**
 * VK ID
 * @param {?} value
 * @return {?}
 */
set vkontakteId(value: string) {
    this.properties.vkontakteId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('vkontakte', this.properties.vkontakteId);
    }
  }
/**
 * Skype ID
 * @param {?} value
 * @return {?}
 */
set skypeId(value: string) {
    this.properties.skypeId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('skype', this.properties.skypeId);
    }
  }
/**
 * email or md5 email related to gravatar
 * @param {?} value
 * @return {?}
 */
set gravatarId(value: string) {
    this.properties.gravatarId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('gravatar', this.properties.gravatarId);
    }
  }
/**
 * Github ID
 * @param {?} value
 * @return {?}
 */
set githubId(value: string) {
    this.properties.githubId = value;
    if (this.properties.width && this.properties.height && this.properties.size) {
      this.loadAvatar('github', this.properties.githubId);
    }
  }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} avatarService
 * @param {?} _sanitizer
 */
constructor(
public elementRef: ElementRef,
    /**@hidden */ templateService: TemplateService,
public avatarService: AvatarService,
private _sanitizer: DomSanitizer) {
    super(elementRef, templateService);
    this.properties = new AvatarModel();
    this.styling = new AvatarStylingModel();
  }
/**
 * @hidden 
 * Init all component data
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('avatar', AvatarTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'BUTTON').then( sampler => {
        if (this.properties.facebookId !== '') { this.loadAvatar('facebook', this.properties.facebookId); }
        if (this.properties.googleId !== '') { this.loadAvatar('google', this.properties.googleId); }
        if (this.properties.twitterId !== '') { this.loadAvatar('twitter', this.properties.twitterId); }
        if (this.properties.gravatarId !== '') { this.loadAvatar('gravatar', this.properties.gravatarId); }
        if (this.properties.vkontakteId !== '') { this.loadAvatar('vkontakte', this.properties.vkontakteId); }
        if (this.properties.githubId !== '') { this.loadAvatar('github', this.properties.githubId); }
        if (this.properties.skypeId !== '') { this.loadAvatar('skype', this.properties.skypeId); }
        let /** @type {?} */ active = new AvatarStylingModel(this.activeTemplate.style);
        this.styling = Object.assign(active, this.styling);
        // console.log('AVATAR', this);
      });
    });
  }
/**
 * @hidden
 * @param {?} name
 * @return {?}
 */
checkInitials(name) {
    if (!!name) {
      let /** @type {?} */ initials = '', /** @type {?} */ names = name.split(' ');
      for (let /** @type {?} */ i = 0; i < this.properties.charsNumber; ++i) { if (names[i]) { initials += names[i].substr(0, 1); } }
      return initials.toUpperCase();
    }
  }
/**
 * @hidden
 * @param {?} type
 * @param {?} id
 * @return {?}
 */
loadAvatar(type, id) {
    this.properties.type = 'image';
    let /** @type {?} */ size = parseFloat(this.properties.size);
    let /** @type {?} */ width = parseFloat(this.properties.width);
    let /** @type {?} */ height = parseFloat(this.properties.height);
    switch (type) {
      case 'skype':
        this.properties.url = this.avatarService.fetchSkypeAvatar(id);
        break;
      case 'facebook':
        this.properties.url = this.avatarService.fetchFacebookAvatar(id, width, height);
        break;
      case 'github':
        this.avatarService.fetchGithubAvatar(id, size).subscribe(url => { this.properties.url = url; });
        break;
      case 'google':
        this.avatarService.fetchGoogleAvatar(id, size).subscribe(url => { this.properties.url = url; });
        break;
      case 'gravatar':
        this.properties.url = this.avatarService.fetchGravatar(id, size);
        break;
      case 'twitter':
        this.properties.url = this.avatarService.fetchTwitterAvatar(id, size);
        break;
      case 'vkontakte':
        this.avatarService.fetchVkontakteAvatar(id, size).subscribe(url => { this.properties.url = url; });
        break;
    }
  }
/**
 * @hidden
 * @param {?} path
 * @return {?}
 */
sanitizeImage(path: string): SafeStyle {
    return this._sanitizer.bypassSecurityTrustStyle('url(' + path + ')');
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-avatar',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"cf-avatar-container {{styling?.container?.class}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\" [style.width]=\"properties?.width || properties?.size\" [style.height]=\"properties?.height || properties?.size\" [style.border]=\"properties?.border\" [style.background]=\"properties?.background\" [style.boxShadow]=\"properties?.shadow | safePipe: 'style'\" [style.borderRadius]=\"properties?.borderRadius\" [style.background-image]=\"sanitizeImage(properties?.backgroundImage)\"> <cf-image *ngIf=\"properties?.type === 'image'\" class=\"cf-avatar-image\" url=\"{{properties?.url}}\" label=\"\" [styling]=\"styling?.image\"> </cf-image> <cf-icon *ngIf=\"properties?.type === 'icon'\" [name]=\"properties?.iconName\" [size]=\"properties?.size\" [style.color]=\"properties?.color\" [styling]=\"styling?.icon\"> </cf-icon> <span *ngIf=\"properties?.type === 'text'\" class=\"cf-avatar-text {{styling?.text?.class}}\" [ngClass]=\"getDynamicClasses(styling?.text?.dynamicClass)\" [style.fontSize]=\"properties?.fontSize\" [style.color]=\"properties?.color\" [innerHtml]=\"properties?.text\"> </span> <span *ngIf=\"properties?.type === 'name'\" class=\"cf-avatar-name {{styling?.name?.class}}\" [ngClass]=\"getDynamicClasses(styling?.name?.dynamicClass)\" [style.fontSize]=\"properties?.fontSize\" [style.color]=\"properties?.color\" [innerHtml]=\"checkInitials(properties.name)\"> </span> </div> ",
  styles: [":host /deep/ .cf-avatar-container { width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; background-size: cover; background-repeat: no-repeat; overflow: hidden; } :host /deep/ .cf-avatar-container img { width: 100%; height: 100%; } :host /deep/ [hidden] { display: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: AvatarService, },
{type: DomSanitizer, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'type': [{ type: Input, args: ['type', ] },],
'name': [{ type: Input, args: ['name', ] },],
'url': [{ type: Input, args: ['url', ] },],
'text': [{ type: Input, args: ['text', ] },],
'iconName': [{ type: Input, args: ['iconName', ] },],
'charsNumber': [{ type: Input, args: ['charsNumber', ] },],
'color': [{ type: Input, args: ['color', ] },],
'size': [{ type: Input, args: ['size', ] },],
'width': [{ type: Input, args: ['width', ] },],
'height': [{ type: Input, args: ['height', ] },],
'fontSize': [{ type: Input, args: ['fontSize', ] },],
'border': [{ type: Input, args: ['border', ] },],
'borderRadius': [{ type: Input, args: ['borderRadius', ] },],
'background': [{ type: Input, args: ['background', ] },],
'backgroundImage': [{ type: Input, args: ['backgroundImage', ] },],
'shadow': [{ type: Input, args: ['shadow', ] },],
'facebookId': [{ type: Input, args: ['facebookId', ] },],
'googleId': [{ type: Input, args: ['googleId', ] },],
'twitterId': [{ type: Input, args: ['twitterId', ] },],
'vkontakteId': [{ type: Input, args: ['vkontakteId', ] },],
'skypeId': [{ type: Input, args: ['skypeId', ] },],
'gravatarId': [{ type: Input, args: ['gravatarId', ] },],
'githubId': [{ type: Input, args: ['githubId', ] },],
};
}

function CfAvatarComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfAvatarComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfAvatarComponent.ctorParameters;
/** @type {?} */
CfAvatarComponent.propDecorators;
/**
 * AvatarModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  type: 'image',
 *  url: 'this.myAvatarUrl',
 *  width: '70px',
 *  height: '70px',
 *  border: '5px solid green',
 *  borderRadius: '50%',
 *  shadow: '-3px -3px 3px 0px rgba(0, 150, 136, 0.3)'
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>type: string</h4>
 * Type of avatar. Can be: image, icon, text. Default: image.
 * <h4>name: string</h4>
 * The name of the person from the initials of which the avatar will be generated, based on <b>charsNumber</b>
 * <h4>url: string</h4>
 * Url for the image
 * <h4>text: string</h4>
 * Text to be used as avatar
 * <h4>iconName: string</h4>
 * Name of icon to be used for cf-icon
 * <h4>charsNumber: number</h4>
 * Number of initials simulators in the avatar. Default: 2.
 * <h4>color: string</h4>
 * The color that will be applied to text or icons
 * <h4>size: any</h4>
 * The size to be applied as the height and width of the avatar.
 * Required if you want use your avatar from other sites (Facebook, Google, etc). Default: 70px.
 * <h4>width: any</h4>
 * The width of the avatar. Has higher priority then <b>size</b>. Default: 70px.
 * <h4>height: any</h4>
 * The height of the avatar. Has higher priority then <b>size</b>. Default: 70px.
 * <h4>fontSize: string</h4>
 * Font size for the text (initials or custom text). Default: 30px.
 * <h4>border: string</h4>
 * Avatar border. Not specified by default.
 * <h4>borderRadius: string</h4>
 * Avatar border radius. Not specified by default.
 * <h4>background: string</h4>
 * Avatar background. Not specified by default.
 * <h4>backgroundImage: string</h4>
 * Avatar background-image. Not specified by default.
 * <h4>shadow: string</h4>
 * Avatar shadow. Not specified by default.
 * <h4>facebookId: string</h4>
 * Facebook ID
 * <h4>googleId: string</h4>
 * Google ID
 * <h4>twitterId: string</h4>
 * Twitter Handle
 * <h4>vkontakteId: string</h4>
 * VK ID
 * <h4>skypeId: string</h4>
 * Skype ID
 * <h4>gravatarId: string</h4>
 * email or md5 email related to gravatar
 * <h4>githubId: string</h4>
 * Github ID
 * @type {?}
 */
CfAvatarComponent.prototype.properties;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * Styling properties for main avatar container
 * <h4>image: ImageStylingModel</h4>
 * Styling properties for cf-image
 * <h4>icon: IconStylingModel</h4>
 * Styling properties for cf-icon
 * <h4>name: StylingModel</h4>
 * Styling properties for name
 * <h4>text: StylingModel</h4>
 * Styling properties for text
 * @type {?}
 */
CfAvatarComponent.prototype.styling;
/**
 * @hidden
 * @type {?}
 */
CfAvatarComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfAvatarComponent.prototype.avatarService;
/**
 * @hidden
 * @type {?}
 */
CfAvatarComponent.prototype._sanitizer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
